// Level system with piecewise curve, points are XP.
// - Levels 1-5: gentle exponential (coeff 1.2), first level after 5 XP
// - Levels 6-15: stable linear using delta at level 5
// - Levels 16+: logarithmic increase to avoid inflation

const START_INCREMENT = 12; // XP to reach level 1 from 0
const EXPO_COEFF = 1.4;
const LOG_GROWTH = 8; // k for logarithmic growth (small increase)

// Delta XP to go from (level-1) to level
export function deltaXpForLevel(level: number): number {
  const l = Math.max(1, Math.floor(level || 1));
  const delta5 = Math.round(START_INCREMENT * Math.pow(EXPO_COEFF, 4));

  if (l >= 1 && l <= 5) {
    return Math.round(START_INCREMENT * Math.pow(EXPO_COEFF, l - 1));
  } else if (l >= 6 && l <= 15) {
    return delta5;
  } else {
    return delta5 + Math.round(LOG_GROWTH * Math.log(l - 14));
  }
}

// XP required to reach a given level (cumulative)
export function xpThresholdForLevel(level: number): number {
  const l = Math.max(0, Math.floor(level || 0));
  if (l <= 0) return 0;
  let sum = 0;
  for (let i = 1; i <= l; i++) sum += deltaXpForLevel(i);
  return sum;
}

// Compute level from total XP (level 1 reached at 5 XP)
export function levelFromXp(xp: number): number {
  const x = Math.max(0, Math.floor(xp || 0));
  let level = 0;
  let sum = 0;
  while (sum + deltaXpForLevel(level + 1) <= x && level < 10000) {
    level += 1;
    sum += deltaXpForLevel(level);
  }
  return level; // can be 0 if xp < 5
}

// XP into current level and to next level
export function xpProgress(xp: number): { level: number; current: number; next: number; inLevel: number; toNext: number; percent: number } {
  const x = Math.max(0, Math.floor(xp || 0));
  const level = levelFromXp(x);
  const currentThreshold = xpThresholdForLevel(level);
  const nextThreshold = xpThresholdForLevel(level + 1);
  const inLevel = Math.max(0, x - currentThreshold);
  const toNext = Math.max(0, nextThreshold - x);
  const span = Math.max(1, nextThreshold - currentThreshold);
  const percent = Math.min(100, Math.max(0, (inLevel / span) * 100));
  return { level, current: currentThreshold, next: nextThreshold, inLevel, toNext, percent };
}
